import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, titleize } from "@/lib/api";
import VenueCard from "@/components/VenueCard";
import { useSEO } from "@/lib/useSEO";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SelectSeparator, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Filter, X } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";

const VENUE_TYPES = [
    "bars","restaurants","pubs","hotels","function-rooms","private-dining-rooms",
    "rooftop-venues","event-spaces","meeting-rooms","conference-venues","wedding-venues",
    "studios","galleries","warehouses","halls","clubs","museums","gardens",
    "boats","mansions","lofts","coworking-spaces","unusual-venues",
];

const EVENT_TYPES = [
    "birthday-parties","private-parties","corporate-events","meetings","weddings",
    "private-dining","christmas-parties","engagement-parties","networking-events",
    "product-launches","conferences","workshops","filming","photoshoots"
];

const AMENITIES = ["WiFi","Bar","Kitchen","Catering","AV Equipment","Outdoor Space","Parking","Late Licence","Air Conditioning","Natural Light"];

function useFilters() {
    const [params, setParams] = useSearchParams();
    const get = (k) => params.get(k) || "";
    const set = (updates) => {
        const p = new URLSearchParams(params);
        Object.entries(updates).forEach(([k, v]) => {
            if (v === null || v === undefined || v === "" || v === "all") p.delete(k);
            else p.set(k, String(v));
        });
        setParams(p);
    };
    return { params, get, set };
}

function FiltersPanel({ get, set, onClose }) {
    return (
        <div className="space-y-5" data-testid="filters-panel">
            <div>
                <Label>Venue type</Label>
                <Select value={get("venue_type") || "all"} onValueChange={(v) => set({ venue_type: v })}>
                    <SelectTrigger data-testid="filter-venue-type"><SelectValue placeholder="Any" /></SelectTrigger>
                    <SelectContent className="max-h-80">
                        <SelectItem value="all">Any venue type</SelectItem>
                        {VENUE_TYPES.map((v) => <SelectItem key={v} value={v}>{titleize(v)}</SelectItem>)}
                    </SelectContent>
                </Select>
            </div>
            <div>
                <Label>Event type</Label>
                <Select value={get("event_type") || "all"} onValueChange={(v) => set({ event_type: v })}>
                    <SelectTrigger data-testid="filter-event-type"><SelectValue placeholder="Any" /></SelectTrigger>
                    <SelectContent className="max-h-80">
                        <SelectItem value="all">Any event type</SelectItem>
                        {EVENT_TYPES.map((v) => <SelectItem key={v} value={v}>{titleize(v)}</SelectItem>)}
                    </SelectContent>
                </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
                <div>
                    <Label>Min capacity</Label>
                    <Input type="number" min="1" value={get("min_capacity")} onChange={(e) => set({ min_capacity: e.target.value })} data-testid="filter-min-capacity" />
                </div>
                <div>
                    <Label>Max capacity</Label>
                    <Input type="number" min="1" value={get("max_capacity")} onChange={(e) => set({ max_capacity: e.target.value })} data-testid="filter-max-capacity" />
                </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
                <div>
                    <Label>Min price</Label>
                    <Input type="number" min="0" value={get("min_price")} onChange={(e) => set({ min_price: e.target.value })} data-testid="filter-min-price" />
                </div>
                <div>
                    <Label>Max price</Label>
                    <Input type="number" min="0" value={get("max_price")} onChange={(e) => set({ max_price: e.target.value })} data-testid="filter-max-price" />
                </div>
            </div>
            <div>
                <Label className="mb-2 block">Amenities</Label>
                <div className="grid grid-cols-2 gap-2">
                    {AMENITIES.map((a) => {
                        const val = get("amenity");
                        return (
                            <label key={a} className="flex items-center gap-2 text-sm cursor-pointer">
                                <Checkbox checked={val === a} onCheckedChange={(c) => set({ amenity: c ? a : "" })} />
                                {a}
                            </label>
                        );
                    })}
                </div>
            </div>
            {onClose && <Button className="w-full mt-4 rounded-full" onClick={onClose}>Apply filters</Button>}
        </div>
    );
}

export default function Search() {
    const { params, get, set } = useFilters();
    const [data, setData] = useState({ items: [], total: 0 });
    const [loading, setLoading] = useState(true);
    const [openFilters, setOpenFilters] = useState(false);

    const qs = useMemo(() => {
        const usp = new URLSearchParams(params);
        return usp.toString();
    }, [params]);

    useEffect(() => {
        setLoading(true);
        api.get(`/venues?${qs}`).then((r) => setData(r.data)).finally(() => setLoading(false));
    }, [qs]);

    const city = get("city");
    const eventType = get("event_type");
    const venueType = get("venue_type");
    const parts = [];
    if (venueType) parts.push(titleize(venueType));
    if (eventType && !venueType) parts.push(`${titleize(eventType)} venues`);
    if (!venueType && !eventType) parts.push("Private venues");
    if (city) parts.push(`in ${titleize(city)}`);
    const heading = parts.join(" ");
    useSEO({
        title: `${heading} | Venuely`,
        description: `Discover ${heading.toLowerCase()} on Venuely. Browse ${data.total} venues, filter by capacity, price and amenities, and enquire in minutes.`,
    });

    return (
        <div className="container-app py-8 md:py-12">
            <div className="flex items-center justify-between gap-4 mb-6">
                <div>
                    <h1 className="font-display text-3xl md:text-4xl font-semibold" data-testid="search-h1">{heading}</h1>
                    <p className="text-sm text-muted-foreground mt-1">{loading ? "Loading…" : `${data.total} venue${data.total === 1 ? "" : "s"} found`}</p>
                </div>
                <div className="flex items-center gap-2">
                    <Select value={get("sort") || "featured"} onValueChange={(v) => set({ sort: v })}>
                        <SelectTrigger className="w-[160px]" data-testid="sort-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="featured">Featured</SelectItem>
                            <SelectItem value="price_asc">Price: low → high</SelectItem>
                            <SelectItem value="price_desc">Price: high → low</SelectItem>
                            <SelectItem value="capacity_desc">Largest capacity</SelectItem>
                            <SelectItem value="newest">Newest</SelectItem>
                        </SelectContent>
                    </Select>
                    <Sheet open={openFilters} onOpenChange={setOpenFilters}>
                        <SheetTrigger asChild>
                            <Button variant="outline" className="rounded-full lg:hidden" data-testid="mobile-filters-btn"><Filter className="h-4 w-4 mr-2" />Filters</Button>
                        </SheetTrigger>
                        <SheetContent side="right" className="w-[340px] overflow-y-auto">
                            <SheetHeader><SheetTitle>Filters</SheetTitle></SheetHeader>
                            <div className="mt-4">
                                <FiltersPanel get={get} set={set} onClose={() => setOpenFilters(false)} />
                            </div>
                        </SheetContent>
                    </Sheet>
                </div>
            </div>

            <div className="grid lg:grid-cols-[280px_1fr] gap-8">
                <aside className="hidden lg:block sticky top-24 self-start rounded-xl border border-border bg-card p-5 max-h-[calc(100vh-8rem)] overflow-y-auto">
                    <FiltersPanel get={get} set={set} />
                </aside>
                <div>
                    {loading ? (
                        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5">
                            {Array.from({ length: 6 }).map((_, i) => <div key={i} className="rounded-xl bg-muted/40 aspect-[4/3] animate-pulse" />)}
                        </div>
                    ) : data.items.length === 0 ? (
                        <div className="rounded-xl border border-dashed border-border p-12 text-center bg-muted/20">
                            <p className="font-display text-2xl">No venues match your filters.</p>
                            <p className="text-muted-foreground mt-2">Try broadening your search or clearing some filters.</p>
                            <Button variant="outline" className="mt-4 rounded-full" onClick={() => set({ venue_type: "", event_type: "", min_capacity: "", max_capacity: "", min_price: "", max_price: "", amenity: "" })}>
                                <X className="h-4 w-4 mr-1" /> Clear filters
                            </Button>
                        </div>
                    ) : (
                        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="venue-grid">
                            {data.items.map((v, i) => <VenueCard key={v.venue_id} venue={v} index={i} />)}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
